import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import HomePage from './pages/HomePage';
import CaseList from './pages/CaseList';
import ReviewList from './pages/ReviewList';
import TaskList from './pages/TaskList';
import SchemeList from './pages/Schemes';
import TariffScheme from './pages/TariffScheme';
import PressReleaseDetail from './pages/PressReleaseDetail';
import PromptWorkbench from './pages/PromptWorkbench';
import PromptEvaluationDetail from './pages/PromptEvaluationDetail';
import PromptVersionEditor from './pages/PromptVersionEditor';
import PromptTestRunner from './pages/PromptTestRunner';

import UnderstaffingFacilityPage from './pages/UnderstaffingFacilityPage';
import UnderstaffingOwnerGroupPage from './pages/UnderstaffingOwnerGroupPage';
import NationalLandscape from './pages/NationalLandscape';
import UnderstaffingScheme from './pages/UnderstaffingScheme';
import Case from './pages/Case';
import Settings from './pages/Settings';
import Explore from './pages/explore/Explore';
import { useAuth } from './context/AuthContext';

function App() {
  const { access, isLoading, user } = useAuth();

  if (isLoading) return null; // or a <LoadingSpinner />

  // Determine default route based on user role
  const getDefaultRoute = () => {
    if (!access) return '/login';
    if (user?.role === 'CASE_REVIEWER') return '/reviews';
    if (user?.role === 'SCHEME_VIEWER') return '/schemes';
    return '/home'; // Standard users go to home page
  };

  // Check if user can access non-scheme pages
  const canAccessFullApp = access && user?.role !== 'SCHEME_VIEWER';
  // Check if user can access scheme pages
  const canAccessSchemes = access && (user?.role !== 'CASE_REVIEWER');

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
        <Route path="/login" element={<Login />} />
        <Route
          path="/home"
          element={canAccessFullApp ? <HomePage /> : <Navigate to={getDefaultRoute()} replace />}
        />
        {/* Routes restricted from SCHEME_VIEWER */}
        <Route
          path="/cases"
          element={canAccessFullApp ? <CaseList /> : <Navigate to={getDefaultRoute()} replace />}
        />
        <Route
          path="/reviews"
          element={canAccessFullApp ? <ReviewList /> : <Navigate to={getDefaultRoute()} replace />}
        />
        <Route
          path="/cases/:caseId"
          element={canAccessFullApp ? <Case /> : <Navigate to={getDefaultRoute()} replace />}
        />
        <Route
          path="/tasks"
          element={canAccessFullApp ? <TaskList /> : <Navigate to={getDefaultRoute()} replace />}
        />
        {/* Explore workbench — Rules / Scans / Leads / Datasets */}
        <Route
          path="/explore"
          element={canAccessFullApp ? <Explore /> : <Navigate to={getDefaultRoute()} replace />}
        />
        <Route
          path="/explore/:type"
          element={canAccessFullApp ? <Explore /> : <Navigate to={getDefaultRoute()} replace />}
        />
        <Route
          path="/explore/:type/:id"
          element={canAccessFullApp ? <Explore /> : <Navigate to={getDefaultRoute()} replace />}
        />
        <Route
          path="/explore/:type/:id/:subType/:subId"
          element={canAccessFullApp ? <Explore /> : <Navigate to={getDefaultRoute()} replace />}
        />
        {/* Scheme routes - accessible by STANDARD and SCHEME_VIEWER */}
        <Route
          path="/schemes"
          element={canAccessSchemes ? <SchemeList /> : <Navigate to={getDefaultRoute()} replace />}
        />
        <Route
          path="/schemes/tariff-coo-fraud"
          element={canAccessSchemes ? <TariffScheme /> : <Navigate to={getDefaultRoute()} replace />}
        />
        {/* FCA Press Release Explorer routes - NOT accessible by SCHEME_VIEWER */}
        <Route
          path="/schemes/explore"
          element={<Navigate to="/schemes/evaluate?tab=explore" replace />}
        />
        <Route
          path="/schemes/explore/:pressReleaseId"
          element={canAccessFullApp ? <PressReleaseDetail /> : <Navigate to={getDefaultRoute()} replace />}
        />
        {/* Prompt Workbench (Explore + Evals + Entity Types) - NOT accessible by SCHEME_VIEWER */}
        <Route
          path="/schemes/evaluate"
          element={canAccessFullApp ? <PromptWorkbench /> : <Navigate to={getDefaultRoute()} replace />}
        />
        <Route
          path="/schemes/evaluate/:pressReleaseId"
          element={canAccessFullApp ? <PromptEvaluationDetail /> : <Navigate to={getDefaultRoute()} replace />}
        />
        {/* Redirects for old routes */}
        <Route
          path="/schemes/prompts"
          element={<Navigate to="/schemes/evaluate?tab=entity-types" replace />}
        />
        <Route
          path="/schemes/entities"
          element={<Navigate to="/schemes/evaluate?tab=entity-types" replace />}
        />
        {/* Prompt Version Editor routes */}
        <Route
          path="/schemes/prompts/editor/:versionId"
          element={canAccessFullApp ? <PromptVersionEditor /> : <Navigate to={getDefaultRoute()} replace />}
        />
        <Route
          path="/schemes/prompts/test/:versionId"
          element={canAccessFullApp ? <PromptTestRunner /> : <Navigate to={getDefaultRoute()} replace />}
        />
        {/* SNF Understaffing: National landscape (PAGE 0) */}
        <Route
          path="/schemes/snf-understaffing"
          element={canAccessSchemes ? <NationalLandscape /> : <Navigate to={getDefaultRoute()} replace />}
        />
        {/* SNF Understaffing: State targeting (PAGE 1) */}
        <Route
          path="/schemes/snf-understaffing/state/:stateCode"
          element={canAccessSchemes ? <UnderstaffingScheme /> : <Navigate to={getDefaultRoute()} replace />}
        />
        {/* Facility detail page */}
        <Route
          path="/schemes/snf-understaffing/facility/:ccn"
          element={canAccessSchemes ? <UnderstaffingFacilityPage /> : <Navigate to={getDefaultRoute()} replace />}
        />
        {/* Owner group detail page */}
        <Route
          path="/schemes/snf-understaffing/owner-group/:groupId"
          element={canAccessSchemes ? <UnderstaffingOwnerGroupPage /> : <Navigate to={getDefaultRoute()} replace />}
        />
        <Route
          path="/schemes/:schemeCode"
          element={canAccessSchemes ? <SchemeList /> : <Navigate to={getDefaultRoute()} replace />}
        />
        <Route
          path="/settings"
          element={canAccessFullApp ? <Settings /> : <Navigate to={getDefaultRoute()} replace />}
        />
        <Route
          path="/schemes/:schemeId/targets/:targetId/scoping"
          element={canAccessFullApp ? <Case /> : <Navigate to={getDefaultRoute()} replace />}
        />
        {/* Legacy route for backward compatibility */}
        <Route
          path="/cases/:caseId/scoping"
          element={canAccessFullApp ? <Case /> : <Navigate to={getDefaultRoute()} replace />}
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
